import * as React from 'react'
import Image from 'next/image'
import _ from 'lodash'

import { styled } from '@mui/material/styles'
import { useTheme } from '@mui/material'
import Stack from '@mui/material/Stack'
import Grid from '@mui/material/Grid'
import Box from '@mui/material/Box'

import { I18n, useTranslation } from 'components/hooks/use-translation'
import Chip from 'components/atomic/atoms/chip'
import Icon from 'components/atomic/atoms/icon'
import Text from 'components/atomic/atoms/text'
import { Path } from 'types/types'

const ImageWrapper = styled('div')`
    position: relative;
    height: 10rem;
    opacity: 0.8;
`

const CardContent = styled('div')`
    border-radius: ${({ theme }) => theme.shape.borderRadius}px;
    border: 1px solid ${({ theme }) => theme.palette.divider};
    height: 100%;
    overflow: hidden;
    display: flex;
    flex-direction: column;
`

interface ProjectProps {
    title: string
    description: string
    thumbnail_url: string | null
    time: number
}

const Project = (props: ProjectProps) => {
    const { t }: I18n = useTranslation()
    const { palette } = useTheme()

    const [descriptionExpanded, setDescriptionExpanded] = React.useState(false)

    return (
        <Grid item xs={12} sm={6} md={4}>
            <CardContent>
                <ImageWrapper>
                    <Image
                        src={props?.thumbnail_url || '/assets/project-placeholder.png'}
                        layout="fill"
                        objectFit="cover"
                        alt="Project thumbnail"
                    />
                </ImageWrapper>
                <Stack spacing={1} p={1} pt={2} pb={2}>
                    <div>
                        <Chip
                            color="primary"
                            size="small"
                            variant="outlined"
                            icon={<Icon fontSize="small" color="primary" name="time" />}
                            sx={{ border: 'none', background: palette.primary.light }}
                            label={t('path:projectSection.time', { value: props.time })}
                            rounded={false}
                        />
                    </div>
                    <Text fontWeight="bold">{props.title}</Text>

                    <span>
                        <Text variant="subtitle2" display="inline">
                            {descriptionExpanded ? props.description : _.truncate(props.description, { length: 100 })}{' '}
                        </Text>
                        {props.description.length > 100 && !descriptionExpanded && (
                            <Text
                                variant="subtitle2"
                                color="primary"
                                sx={{ display: 'inline', textDecoration: 'underline' }}
                                onClick={() => setDescriptionExpanded(true)}>
                                {t('path:projectSection.readMore')}
                            </Text>
                        )}
                    </span>
                </Stack>
            </CardContent>
        </Grid>
    )
}

interface ProjectsProps {
    projects: Path['projects']
}

const Projects = (props: ProjectsProps) => {
    const { t }: I18n = useTranslation()

    if (!props.projects.length) return null

    return (
        <div>
            <Box mb={4}>
                <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                    <Icon fontSize="small" color="primary" name="handyman" />
                    <Text sx={{ textTransform: 'uppercase', fontWeight: 'bold' }} color="primary">
                        {t('path:projectSection.title')}
                    </Text>
                </Stack>
                <Text variant="h4">{t('path:projectSection.projectsTitle')}</Text>
            </Box>
            <Stack gap="1rem">
                <Grid spacing={2} container>
                    {props.projects.map((item) => (
                        <Project key={item._id} {...item} />
                    ))}
                </Grid>
            </Stack>
        </div>
    )
}

export default Projects
